'use client'

import { Parse, Redirect } from '@/components/Core'
import Button from '@/components/Ui/Button'
import { Checkbox, CheckboxGroup } from '@/components/Ui/Checkbox'
import { Description } from '@/components/Ui/Field'
import { Radio, RadioGroup } from '@/components/Ui/RadioGroup'
import { Select, SelectItem } from '@/components/Ui/Select'
import { TextField } from '@/components/Ui/TextField'
import FormFragment from '@/lib/fragments/forms/form'
import { useStoreTransition } from '@/store'
import { Lib } from '@/types'
import { useForm } from '@formspree/react'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { useMemo } from 'react'
//import $ from './style.module.scss'

const ReCaptcha = dynamic(() => import('./Components/ReCaptcha'))
interface FormBuilderProps extends React.HTMLAttributes<HTMLFormElement> {
  data: Lib.FragmentOf<typeof FormFragment>
  messagesClassName?: string
}

const FormBuilder = ({ ...props }: FormBuilderProps) => {
  const { data, className, messagesClassName, ...rest } = props
  const { formBuilder } = data
  const [state, handleSubmit] = useForm(data.formspreeId)

  const setIsTransitionOut = useStoreTransition.use.setIsTransitionOut()
  const setTranistionType = useStoreTransition.use.setTransitionType()

  const renderedForm = useMemo(() => {
    if (!formBuilder || !formBuilder.length) return null
    return formBuilder.map((field, k) => {
      switch (field.__typename) {
        case 'TextFieldRecord':
          return (
            <TextField
              id={field.fieldId}
              name={field.fieldId}
              key={k}
              placeholder={field?.placeholder || undefined}
              label={field.label}
              isRequired={field.required}
              //textarea={field.variant === 'text-area'}
              type={field.variant === 'email' ? 'email' : 'text'}
              className={clsx(!field?.halfSize && 'md:col-span-2')}
              //className={clsx($.field, field.halfSize && $.half)}
            />
          )
        case 'FormFieldSelectRecord':
          return (
            <Select
              isRequired={field.required}
              name={field.fieldId}
              id={field.fieldId}
              key={k}
              label={field.label}
              placeholder={field.placeholder || undefined}
              className={clsx(!field?.halfSize && 'md:col-span-2')}
              //selectFieldClassName={clsx($.field, field.halfSize && $.half)}
            >
              {field.items.map((item) => (
                <SelectItem id={item.value} key={item.value}>
                  {item.title}
                </SelectItem>
              ))}
            </Select>
          )
        case 'FormFieldRadioGroupRecord':
          return (
            <RadioGroup
              key={k}
              {...field}
              isRequired={field.required}
              className={clsx(!field?.halfSize && 'md:col-span-2')}
              orientation="horizontal"
              name={field.fieldId}
              label={field.label}
            >
              {field.items.map((item) => (
                <Radio key={item.value} id={item.value} value={item.value}>
                  {item.title}
                </Radio>
              ))}
            </RadioGroup>
          )
        case 'FormFieldSepratorRecord':
          return (
            <div
              key={k}
              className={clsx(!field?.halfSize && 'md:col-span-2')}
              //className={clsx($.separator, field.halfSize && $.half)}
            />
          )
        case 'FormFieldCheckboxRecord':
          return (
            <CheckboxGroup
              key={k}
              isRequired={field.required}
              className={clsx(
                !field?.halfSize && 'flex flex-row md:col-span-2'
              )}
              //className={clsx($.field, field.halfSize && $.half)}
              name={field.fieldId}
              label={field.label}
            >
              <Checkbox
                key={k}
                {...field}
                id={field.fieldId}
                name={field.fieldId}
                isRequired={field.required}
                className={clsx(!field?.halfSize && 'md:col-span-2')}
                isSelected={field.checked}
              >
                <Parse html={field.label} />
              </Checkbox>
              {field.infoLink && (
                <Description>
                  <Link
                    className="underline hover:no-underline"
                    target="_blank"
                    href={`/${field.infoLink.slug}`}
                  >
                    {field.infoLink.pageTitle}
                  </Link>
                </Description>
              )}
            </CheckboxGroup>
          )
        case 'FormFieldSubmitRecord':
          return (
            <div className="md:col-start-2 md:flex md:justify-end" key={k}>
              <Button
                key={k}
                type="submit"
                as="button"
                disabled={state.submitting}
                isNext={false}
                color="brand"
                className={'pointer-events-auto cursor-pointer'}
              >
                {field.buttonText}
              </Button>
            </div>
          )
        case 'FormFieldRecaptchaRecord':
          return (
            <ReCaptcha
              key={k}
              data={field}
              //className={clsx($.field, field.halfSize && $.half)}
            />
          )

        default:
          return null
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formBuilder])

  if (state.succeeded && data.redirect) {
    setTranistionType('fade')
    setIsTransitionOut(true)
    return <Redirect data={data.redirectLink} />
  }

  const messageBannerClassName =
    'p-md overflow-hidden dark:bg-neutral-800 [&_span]:z-10 [&_span]:relative rounded-sm mt-md gradient-bg relative text-white text-sm font-semibold'

  return (
    <>
      <form
        className={clsx('gap-lg grid md:grid-cols-2', className)}
        onSubmit={handleSubmit}
        {...rest}
      >
        {renderedForm}
      </form>
      {((state.succeeded && !data.redirect) || state.submitting) && (
        <div className={clsx('', messageBannerClassName, messagesClassName)}>
          <span>
            {state.succeeded ? (
              <Parse html={data.successMessage} />
            ) : (
              <Parse html={data.pendingMessage} />
            )}
          </span>
        </div>
      )}
    </>
  )
}

export default FormBuilder
